import { ReviewsStrip } from "@/components/SocialLinks";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, ArrowUpRight, CalendarCheck, Check } from "lucide-react";
import GoogleReviewCta from "@/components/GoogleReviewCta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  { value: "morning", label: "Morning (8am – 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm – 5pm)" },
  { value: "evening", label: "Early evening (5pm – 7pm)" },
];

const BookingModal = ({ open, onClose, defaultService = "" }) => {
  if (!open) return null;
  return <BookingModalInner onClose={onClose} defaultService={defaultService} />;
};

const BookingModalInner = ({ onClose, defaultService }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    preferred_time: "",
    site_postcode: "",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        service_interest: `Site survey · ${defaultService || "General"}`,
        message: `Booking request — preferred ${form.preferred_time || "any time"}. Site postcode: ${form.site_postcode || "—"}. Notes: ${form.notes || "—"}.`,
        source: "booking",
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail?.[0]?.msg || err?.message || "Could not submit");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-ink/40 backdrop-blur-sm" data-testid="booking-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-xl rounded-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-black/8 flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-overline uppercase text-accent">
            <CalendarCheck className="h-3.5 w-3.5" /> [ Book a free site survey ]
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="booking-close" className="text-muted hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>

        {status === "done" ? (
          <div className="p-8 md:p-12 text-center" data-testid="booking-success">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent mb-5">
              <Check className="h-5 w-5" strokeWidth={3} />
            </span>
            <h3 className="font-display font-bold text-[1.5rem] tracking-tightest mb-3">Thank you, {form.first_name || "we&rsquo;ve got your request"}.</h3>
            <p className="text-[14px] text-muted-foreground leading-relaxed max-w-sm mx-auto mb-6">
              We&rsquo;ll be in touch the same working day to confirm a time that suits your site.
            </p>
            <button onClick={onClose} className="inline-flex items-center gap-2 px-6 py-3 border border-ink text-ink text-[13px] font-semibold rounded-sm hover:bg-ink hover:text-white transition-colors">
              Close
            </button>
            <GoogleReviewCta variant="card" />
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-10 space-y-5" data-testid="booking-form">
            <h2 className="font-display font-bold text-[1.5rem] md:text-[1.75rem] tracking-tightest leading-tight">
              Book a free 30-minute site survey.
            </h2>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              No obligation — a Calmbreeze engineer comes to your London site, reviews your HVAC,
              and follows up with a fixed-price quote.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="First name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand" data-testid="booking-first" />
              <input required placeholder="Last name *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand" data-testid="booking-last" />
              <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand" data-testid="booking-email" />
              <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand" data-testid="booking-phone" />
              <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand md:col-span-2" data-testid="booking-company" />
              <input placeholder="Site postcode (e.g. UB7 9FF)" value={form.site_postcode} onChange={(e) => setForm({ ...form, site_postcode: e.target.value })} className="bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand md:col-span-2" data-testid="booking-postcode" />
            </div>

            <div>
              <div className="font-mono text-[10px] tracking-overline uppercase text-muted mb-2">Preferred call time</div>
              <div className="flex flex-wrap gap-2">
                {TIME_SLOTS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setForm({ ...form, preferred_time: s.value })}
                    data-testid={`booking-time-${s.value}`}
                    className={`px-3 py-2 rounded-sm border text-[13px] transition-colors ${
                      form.preferred_time === s.value
                        ? "border-brand bg-brand text-white"
                        : "border-black/10 bg-white hover:border-black/30"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea rows={3} placeholder="Anything we should know about your site?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-paper-cool/60 border border-black/8 rounded-sm px-3 py-3 text-[14px] focus:outline-none focus:border-brand resize-none" data-testid="booking-notes" />

            {status === "error" && (
              <p className="text-[12px] text-accent font-mono" data-testid="booking-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="booking-submit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white text-[14px] font-semibold rounded-sm hover:bg-brand transition-colors disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Request my survey"} <ArrowUpRight className="h-4 w-4" />
            </button>
            <ReviewsStrip testId="booking-reviews" />
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
